import { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  GoogleAuthProvider,
  signInWithRedirect,
  signInWithPopup,
  getRedirectResult,
  signInAnonymously,
  EmailAuthProvider,
  linkWithCredential,
  sendPasswordResetEmail,
  reauthenticateWithCredential,
  updatePassword,
  updateProfile,
  sendEmailVerification
} from "firebase/auth";
import { auth, db } from '../config/firebase-config';
import { doc, getDoc } from "firebase/firestore";

import { requestAnonSession, requestSignUp, requestLogOut } from '../services/userService';
import i18n, { normalizeLanguage } from '../i18n';

const userAuthContext = createContext();

export function UserAuthContextProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);


  async function logIn(email, password) {
    await signInWithEmailAndPassword(auth, email, password);
    const currentUser = auth.currentUser;
    const userDoc = await getDoc(doc(db, "users", currentUser.uid));
    const userData = {
      ...userDoc.data(),
      uid: currentUser.uid,
      accessToken: await currentUser.getIdToken()
    };
    setUser(userData);
    return currentUser;
  }


  async function forgotPassword(email) {
    // Optional: if you want the link to land on your app after reset:
    const actionCodeSettings = {
      url: `${window.location.origin}/login-page`, // where to redirect after reset
      handleCodeInApp: false,
    };
    await sendPasswordResetEmail(auth, email, actionCodeSettings);
  }


  // Proves the person at the keyboard knows the current password.
  //
  // Firebase's own client SDK refuses to change a password or an email on a
  // stale login (auth/requires-recent-login), but our backend changes both
  // through the Admin SDK, which skips that check entirely. So without this
  // call, anyone who finds an unlocked browser can take the account over. Call
  // it immediately before any account-critical change.
  async function reauthenticate(currentPassword) {
    const currentUser = auth.currentUser;
    if (!currentUser?.email) {
      const error = new Error("No signed-in Firebase user");
      error.code = "auth/no-current-user";
      throw error;
    }
    const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
    await reauthenticateWithCredential(currentUser, credential);
  }


  // The password lives only in Firebase Auth -- neither the users doc nor the
  // Express session mirrors it -- so this goes straight to the SDK rather than
  // through /codenec/edit-account. The reauth above has just refreshed the
  // login, so `requires-recent-login` cannot fire here.
  async function changePassword(currentPassword, newPassword) {
    await reauthenticate(currentPassword);
    await updatePassword(auth.currentUser, newPassword);
  }


  // The email change runs server-side through the Admin SDK, so the SDK's
  // cached copy of the profile (auth.currentUser.email) keeps the old address
  // until it is reloaded -- and reauthenticate() builds its credential from
  // exactly that field. Without this, changing the email then the password in
  // one sitting fails on the stale address.
  async function reloadAuthUser() {
    if (auth.currentUser) await auth.currentUser.reload();
  }


  // The register form's name is sent to the Firestore users doc and nowhere
  // else, so the Firebase Auth profile carries no displayName of its own. That
  // is exactly what the verification email prints through %DISPLAY_NAME%, and
  // the console will not let us edit that template body -- so without this the
  // email greets every new student as "Hello ,". Run it BEFORE sendVerification.
  //
  // Never throws, for the same reason sendVerification does not: the account
  // already exists by this point and a cosmetic profile field is not worth
  // failing it over.
  async function setDisplayName(currentUser, name, last) {
    try {
      const displayName = `${name ?? ""} ${last ?? ""}`.trim();
      if (displayName) await updateProfile(currentUser, { displayName });
    } catch (err) {
      console.error("Failed to set displayName:", err);
    }
  }

  // Firebase picks which localized template to send from auth.languageCode.
  // Nothing else in this app ever sets it, so before this every verification
  // AND password-reset email went out in English regardless of the UI language
  // -- on a Spanish-first product.
  //
  // Awaited but NEVER throws. RegisterToPlan redirects to Stripe the moment
  // signUp() returns, so letting auth/too-many-requests bubble up here would
  // fail an account that was already created successfully; and awaiting rather
  // than firing-and-forgetting keeps that redirect from cancelling the request
  // in flight. A missing verification email is recoverable, a signup that
  // throws after the Firebase user exists is not.
  async function sendVerification(currentUser) {
    try {
      auth.languageCode = normalizeLanguage(i18n.language);
      try {
        await sendEmailVerification(currentUser, {
          url: `${window.location.origin}/home`,
          handleCodeInApp: false,
        });
      } catch (err) {
        // auth/unauthorized-continue-uri means this origin is missing from
        // Firebase's Authorized domains list. Since the outer catch is silent,
        // that would otherwise send NO email and say nothing. Retry without a
        // continue URL: the student still gets a working link, they just land
        // on Firebase's own confirmation page instead of back in the app.
        if (err?.code !== "auth/unauthorized-continue-uri") throw err;
        await sendEmailVerification(currentUser);
      }
    } catch (err) {
      console.error("Verification email failed to send:", err);
    }
  }

  // legalAcceptance: { termsVersion, ageConfirmed, residenceCountry, locale,
  // privacyNotice? } from the sign-up form (legalAcceptance() in
  // config/legal.js). The backend refuses a NEW profile without it, or with a
  // country it does not serve, and ignores it for a profile that already
  // exists. The profile's copy is user.legalAcceptance; until the profile is
  // read again, the state carries what was sent (pages read residenceCountry).
  async function signUp(email, password, role, name, last, legalAcceptance) {
    const credential = EmailAuthProvider.credential(email, password);
    if (auth.currentUser?.isAnonymous) {
      try {
        const linkedCredential = await linkWithCredential(auth.currentUser, credential);
        const currentUser = linkedCredential.user;
        await requestSignUp(email, role, name, last, await currentUser.getIdToken(), legalAcceptance);
        await setDisplayName(currentUser, name, last);
        await sendVerification(currentUser);
        setUser({ ...currentUser, role, name, last, ...(legalAcceptance ? { legalAcceptance } : {}) });
        return currentUser
      } catch (err) {
        console.error("Failed to link anonymous user:", err);
      }
    } else {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const currentUser = userCredential.user;
      await requestSignUp(email, role, name, last, await currentUser.getIdToken(), legalAcceptance);
      await setDisplayName(currentUser, name, last);
      await sendVerification(currentUser);
      setUser({ ...currentUser, role, name, last, ...(legalAcceptance ? { legalAcceptance } : {}) });
      return currentUser
    }
  }

  async function logOut() {
    try {
      await requestLogOut();
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  }


  async function googleSignUp(legalAcceptance) {
    // console.log("googleSignIn CALLED");
    const googleAuthProvider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, googleAuthProvider);
    // console.log("PopUp RESULT:", result);

    const currentUser = result.user;

    const token = await currentUser.getIdToken();

    const userRef = doc(db, "users", currentUser.uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {

      const fullName = currentUser.displayName || "";
      const parts = fullName.trim().split(" ");
      const name = parts[0] || "";
      const last = parts.slice(1).join(" ");

      await requestSignUp(currentUser.email, "user", name, last, token, legalAcceptance);
    }

    const updatedUserDoc = await getDoc(userRef);

    const finalUser = {
      ...currentUser,
      ...updatedUserDoc.data(),
      accessToken: token
    };


    setUser(finalUser);
    return finalUser;
  }


  async function googleSignIn() {
    const googleAuthProvider = new GoogleAuthProvider();

    const result = await signInWithPopup(auth, googleAuthProvider);
    const currentUser = result.user;
    const token = await currentUser.getIdToken();

    const userRef = doc(db, "users", currentUser.uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error("No account found for this Google user.");
    }

    const finalUser = {
      ...currentUser,
      ...userDoc.data(),
      accessToken: token
    };

    setUser(finalUser);
    return finalUser;
  }


  async function signInAnonUser() {
    if (!auth.currentUser) {
      await signInAnonymously(auth);
    }
    const token = await auth.currentUser.getIdToken();
    const sessionUser = await requestAnonSession(token);
    const role = sessionUser.user.role;
    const enriched = {
      ...auth.currentUser,
      role
    };
    setUser(enriched);
    return enriched;
  }


  // Re-fetch the signed-in user's Firestore doc and merge it into state.
  // Used after returning from Stripe Checkout so the client picks up
  // webhook-granted entitlement (courseAccess / subscriptionStatus) without a
  // full page reload. Returns the merged user, or null if nobody is signed in.
  async function refreshUser() {
    const currentUser = auth.currentUser;
    if (!currentUser) return null;
    try {
      const userDoc = await getDoc(doc(db, "users", currentUser.uid));
      const merged = {
        ...currentUser,
        ...(userDoc.exists() ? userDoc.data() : {}),
        accessToken: await currentUser.getIdToken(),
      };
      setUser(merged);
      return merged;
    } catch (error) {
      console.error("Failed to refresh user:", error);
      return null;
    }
  }




  // currently not using this useEffect. it suupose to fetch for  the google redirects after using signInWithRedirect, but it is not working properly since it returns null for result every time. 
  useEffect(() => {
    // console.log("fetchRedirectResult LOG");
    const fetchRedirectResult = async () => {
      try {
        // console.log("CHECKING REDIRECT RESULT");
        const result = await getRedirectResult(auth);
        // console.log("REDIRECT RESULT:", result);
        // console.log("CURRENT USER:", auth.currentUser);

        const currentUser = result?.user || auth.currentUser;
        if (!currentUser) return;

        const token = await currentUser.getIdToken();
        const userRef = doc(db, "users", currentUser.uid);
        // Profiles are only created from the sign-up forms, which carry the legal acceptance.
        const userDoc = await getDoc(userRef);

        setUser({
          ...currentUser,
          ...userDoc.data(),
          accessToken: token
        });
      } catch (error) {
        console.error("Google redirect handling failed:", error);
      }
    };

    fetchRedirectResult();
  }, []);


  useEffect(() => {
    // console.log("onAuthStateChanged LOG");
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        let enrichedUser = currentUser;

        // ✅ No longer fetch session here for anonymous — handled in signInAnonUser
        if (!currentUser.isAnonymous) {
          try {
            const userDoc = await getDoc(doc(db, "users", currentUser.uid));
            if (userDoc.exists()) {
              enrichedUser = { ...currentUser, ...userDoc.data() };
            }
          } catch (error) {
            console.error("Failed to enrich signed-in user:", error);
          }
        }

        setUser(enrichedUser);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      // console.log("Updated user:", user);
    }
  }, [user]);

  return (
    <userAuthContext.Provider
      value={{ user, setUser, signUp, logOut, logIn, googleSignIn, googleSignUp, signInAnonUser, forgotPassword, refreshUser, reauthenticate, changePassword, reloadAuthUser }}
    >
      {!loading && children}
    </userAuthContext.Provider>
  );
}

export function useUserAuth() {
  return useContext(userAuthContext);
}
